import api from '@/store/api';
import { athena, boro } from '@/store/api/instances';
import type { Route as ServiceRoute } from '@/types/route';
import type { ApiListingRequest, ApiResponse } from '@bitstopco/bitstop-theme';

interface CreateRouteRequest {
  name?: string;
  technician_id?: string;
  scheduled_at?: string;
  notes?: string;
}

interface UpdateRouteRequest {
  name?: string | null;
  technician_id?: string | null;
  scheduled_at?: string | null;
  route_status?: string;
  notes?: string | null;
}

interface AssignServiceTicketToRouteRequest {
  route_id: string;
  service_ticket_id: string;
}

interface BulkAssignServiceTicketsToRouteRequest {
  route_id: string;
  service_ticket_ids: string[];
}

interface BulkAssignServiceTicketResult {
  service_ticket_id: string;
  success: boolean;
  error?: string;
}

export const routesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getRoutes: build.query<ApiResponse<ServiceRoute[]>, ApiListingRequest>({
      query: (data) =>
        athena({
          url: 'v2/Operations/routes',
          method: 'POST',
          data,
        }),
      providesTags: ['Routes'],
    }),
    getRoute: build.query<ApiResponse<ServiceRoute>, string>({
      query: (id) =>
        athena({
          url: `v2/Operations/routes/${id}`,
          method: 'GET',
        }),
      providesTags: (_result, _error, id) => [{ type: 'Routes', id }],
    }),
    createRoute: build.mutation<ApiResponse<ServiceRoute>, CreateRouteRequest>({
      query: (data) =>
        boro({
          url: 'v2/Operations/routes',
          method: 'POST',
          data,
        }),
      invalidatesTags: ['Routes'],
    }),
    updateRoute: build.mutation<ApiResponse<ServiceRoute>, UpdateRouteRequest & { id: string }>({
      query: ({ id, ...data }) =>
        boro({
          url: `v2/Operations/routes/${id}`,
          method: 'PATCH',
          data,
        }),
      invalidatesTags: (_, __, { id }) => ['Routes', { type: 'Routes', id }],
    }),
    assignServiceTicketToRoute: build.mutation<
      ApiResponse<ServiceRoute>,
      AssignServiceTicketToRouteRequest
    >({
      query: ({ route_id, service_ticket_id }) =>
        boro({
          url: `v2/Operations/routes/${route_id}/service-tickets/${service_ticket_id}`,
          method: 'POST',
        }),
      invalidatesTags: (_, __, { route_id, service_ticket_id }) => [
        'Routes',
        { type: 'Routes', id: route_id },
        'ServiceTickets',
        // Refetch the single service ticket so its embedded routes update.
        { type: 'ServiceTickets', id: service_ticket_id },
      ],
    }),
    bulkAssignServiceTicketsToRoute: build.mutation<
      ApiResponse<{ results: BulkAssignServiceTicketResult[] }>,
      BulkAssignServiceTicketsToRouteRequest
    >({
      query: ({ route_id, service_ticket_ids }) =>
        boro({
          url: `v2/Operations/routes/${route_id}/service-tickets/bulk`,
          method: 'POST',
          data: { service_ticket_ids },
        }),
      invalidatesTags: (_, __, { route_id, service_ticket_ids }) => [
        'Routes',
        { type: 'Routes', id: route_id },
        'ServiceTickets',
        // Refetch each affected single service ticket so embedded routes update.
        ...service_ticket_ids.map((id) => ({ type: 'ServiceTickets' as const, id })),
      ],
    }),
    removeServiceTicketFromRoute: build.mutation<
      ApiResponse<ServiceRoute>,
      AssignServiceTicketToRouteRequest
    >({
      query: ({ route_id, service_ticket_id }) =>
        boro({
          url: `v2/Operations/routes/${route_id}/service-tickets/${service_ticket_id}`,
          method: 'DELETE',
        }),
      invalidatesTags: (_, __, { route_id, service_ticket_id }) => [
        'Routes',
        { type: 'Routes', id: route_id },
        'ServiceTickets',
        // Refetch the single service ticket so its embedded routes update.
        { type: 'ServiceTickets', id: service_ticket_id },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetRoutesQuery,
  useGetRouteQuery,
  useCreateRouteMutation,
  useUpdateRouteMutation,
  useAssignServiceTicketToRouteMutation,
  useBulkAssignServiceTicketsToRouteMutation,
  useRemoveServiceTicketFromRouteMutation,
} = routesApi;

export const {
  endpoints: { getRoutes, getRoute, createRoute, updateRoute, assignServiceTicketToRoute },
} = routesApi;
